import Link from '../Link'
import LanguageSelect from '../LanguageSelect'
import { HeroIcons } from '../../../lib/utils/heroIcons'
import { useEffect, useRef, useState, type CSSProperties } from 'react'
import gtmService, { DestinationType } from '../../../services/gtm.service'

export interface HeaderLinkItem {
  href: string
  label: string
  target?: string
  skipLocaleHandling?: boolean
}

interface HeaderProps {
  isMenuOpen: boolean
  onToggleMenu: () => void
  brandHref: string
  logoAlt: string
  desktopLinks: HeaderLinkItem[]
  mobileLinks: HeaderLinkItem[]
  positionClassName?: string
  navSpacingClassName?: string
  containerClassName?: string
  ctaLink?: HeaderLinkItem
  ctaLabel?: string
  mobileCtaLabel?: string
  mobileMenuCtaLabel?: string
  menuAriaLabel?: string
}

const parseRgb = (value: string) => {
  const match = value.match(/rgba?\(([^)]+)\)/)
  if (!match) return null
  const [r, g, b, a = 1] = match[1].split(',').map((part) => parseFloat(part))
  return { r, g, b, a }
}

// Perceived brightness (0 = black, 1 = white).
const relativeLuminance = ({ r, g, b }: { r: number; g: number; b: number }) =>
  (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255

const GLASS_TRANSITION =
  'background-color 450ms ease, backdrop-filter 450ms ease, -webkit-backdrop-filter 450ms ease, border-color 450ms ease, box-shadow 450ms ease'

const getDestinationType = (href: string): DestinationType => {
  if (href.includes('/register')) return 'app_register'
  if (href.includes('/login')) return 'app_login'
  if (href.includes('/properties')) return 'app_properties'
  if (href.includes('/contact')) return 'contact'
  if (href.includes('/newsletter')) return 'newsletter'
  if (href.includes('/blog')) return 'blog'
  if (href.startsWith('http')) return 'external'

  return 'internal'
}

// Frosted: opaque light surface so the (dark) logo pops over similar-coloured
// backgrounds. Clear: transparent "liquid glass" for contrasting backgrounds.
const FROSTED_GLASS: CSSProperties = {
  backgroundColor: 'rgba(255,255,255,0.82)',
  backdropFilter: 'blur(26px) saturate(150%)',
  WebkitBackdropFilter: 'blur(26px) saturate(150%)',
  borderColor: 'rgba(255,255,255,0.80)',
  boxShadow:
    'inset 0 1px 0 rgba(255,255,255,0.9), 0 12px 30px rgba(18,15,62,0.18)',
}

const CLEAR_GLASS: CSSProperties = {
  backgroundColor: 'rgba(255,255,255,0.30)',
  backdropFilter: 'blur(10px) saturate(195%)',
  WebkitBackdropFilter: 'blur(10px) saturate(195%)',
  borderColor: 'rgba(255,255,255,0.55)',
  boxShadow:
    'inset 0 1px 0 rgba(255,255,255,0.95), inset 0 -8px 18px rgba(120,110,226,0.12), 0 14px 36px rgba(18,15,62,0.24)',
}

export default function Header({
  isMenuOpen,
  onToggleMenu,
  brandHref,
  logoAlt,
  desktopLinks,
  mobileLinks,
  positionClassName = 'absolute top-0',
  navSpacingClassName = 'p-3',
  containerClassName = 'mx-auto w-full px-5 md:px-10 lg:px-16 xl:px-24 2xl:px-44',
  ctaLink,
  ctaLabel = 'Investir maintenant',
  mobileCtaLabel,
  mobileMenuCtaLabel = 'Créer un compte',
  menuAriaLabel = 'Toggle menu',
}: HeaderProps) {
  const launchAppLink = desktopLinks[0]
  const resolvedCtaLink = ctaLink || launchAppLink
  const resolvedMobileCtaLabel = mobileCtaLabel || ctaLabel

  const navRef = useRef<HTMLElement>(null)
  const logoRef = useRef<HTMLDivElement>(null)
  // Default to frosted so the logo stays legible before the first sample
  // (the landing hero sits behind the navbar on load).
  const [isFrosted, setIsFrosted] = useState(true)

  useEffect(() => {
    const nav = navRef.current
    const logo = logoRef.current
    if (!nav || !logo) return

    let frame = 0

    const sample = () => {
      frame = 0
      const rect = logo.getBoundingClientRect()
      // Sample over the logo mark on the left — that's where the logo blends
      // into a same-coloured background, not the middle of the wordmark.
      const x = rect.left + rect.height / 2
      const y = rect.top + rect.height / 2

      // Look through the navbar to whatever is rendered behind the logo.
      const previousPointerEvents = nav.style.pointerEvents
      nav.style.pointerEvents = 'none'
      const behind = document.elementFromPoint(x, y)
      nav.style.pointerEvents = previousPointerEvents
      if (!behind) return

      // Image/gradient backgrounds expose no readable CSS colour — sections can
      // declare their intent explicitly via data-navtheme="frosted|clear".
      const themed = behind.closest('[data-navtheme]')
      if (themed) {
        setIsFrosted(themed.getAttribute('data-navtheme') === 'frosted')
        return
      }

      // Otherwise read the first opaque background colour behind the logo and
      // frost the navbar when that colour is dark (close to the logo's tone).
      // Read the first opaque background colour behind the logo and frost when
      // it is dark. Image backgrounds expose no colour, so dark images that can
      // hide the logo are tagged with data-navtheme (handled above).
      let node: Element | null = behind
      while (node && node !== document.documentElement) {
        const color = parseRgb(getComputedStyle(node).backgroundColor)
        if (color && color.a >= 0.5) {
          setIsFrosted(relativeLuminance(color) < 0.6)
          return
        }
        node = node.parentElement
      }
      setIsFrosted(false)
    }

    const onScroll = () => {
      if (frame) return
      frame = requestAnimationFrame(sample)
    }

    sample()
    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', onScroll)
    return () => {
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onScroll)
      if (frame) cancelAnimationFrame(frame)
    }
  }, [])

  const barStyle: CSSProperties = {
    ...(isFrosted ? FROSTED_GLASS : CLEAR_GLASS),
    transition: GLASS_TRANSITION,
  }

  return (
    <nav
      ref={navRef}
      className={`${positionClassName} ${navSpacingClassName} z-50 w-full`}
    >
      <div className={containerClassName}>
        <div
          className="flex w-full items-center justify-between rounded-2xl border px-2 py-2 tablet:px-3 tablet:py-3"
          style={barStyle}
        >
          <Link href={brandHref}>
            <div ref={logoRef} className="flex items-center space-x-1">
              <span
                className="block h-8 w-[151px] bg-gradient [-webkit-mask-image:url('/img/logos/shelters/logotype-white.svg')] [-webkit-mask-position:left_center] [-webkit-mask-repeat:no-repeat] [-webkit-mask-size:contain] [mask-image:url('/img/logos/shelters/logotype-white.svg')] [mask-position:left_center] [mask-repeat:no-repeat] [mask-size:contain] tablet:h-10 tablet:w-[189px]"
                aria-label={logoAlt}
                role="img"
              />
            </div>
          </Link>

          <div className="hidden items-center gap-8 text-[16px] font-normal text-[#293254] desktop:flex">
            {desktopLinks.map(({ href, label, target, skipLocaleHandling }) => (
              <Link
                key={`${href}-${label}`}
                href={href}
                target={target}
                skipLocaleHandling={skipLocaleHandling}
                onClick={() =>
                  gtmService.ctaClicked({
                    ctaId: `header_nav_${label}`,
                    ctaLabel: label,
                    ctaLocation: 'header_nav',
                    destinationUrl: href,
                    destinationType: getDestinationType(href),
                  })
                }
                className="transition-colors hover:text-sheltersBlue"
              >
                {label}
              </Link>
            ))}
          </div>

          <div className="hidden items-center gap-4 desktop:flex">
            <div className="text-[#293254]">
              <LanguageSelect />
            </div>
            <Link
              href={resolvedCtaLink?.href || '#'}
              target={resolvedCtaLink?.target}
              skipLocaleHandling={resolvedCtaLink?.skipLocaleHandling}
              onClick={() =>
                gtmService.ctaClicked({
                  ctaId: 'header_desktop_cta',
                  ctaLabel,
                  ctaLocation: 'header',
                  destinationUrl: resolvedCtaLink?.href || '#',
                  destinationType: getDestinationType(
                    resolvedCtaLink?.href || '#',
                  ),
                })
              }
              className="inline-flex h-11 items-center justify-center rounded-button bg-gradient px-5 text-[15px] font-medium text-white shadow-[0_6px_18px_rgba(51,40,154,0.24)] transition-all duration-300 hover:-translate-y-0.5 hover:brightness-110"
            >
              {ctaLabel}
            </Link>
          </div>

          <div className="flex items-center gap-2 desktop:hidden">
            <Link
              href={resolvedCtaLink?.href || '#'}
              target={resolvedCtaLink?.target}
              skipLocaleHandling={resolvedCtaLink?.skipLocaleHandling}
              onClick={() =>
                gtmService.ctaClicked({
                  ctaId: 'header_mobile_cta',
                  ctaLabel: resolvedMobileCtaLabel,
                  ctaLocation: 'header',
                  destinationUrl: resolvedCtaLink?.href || '#',
                  destinationType: getDestinationType(
                    resolvedCtaLink?.href || '#',
                  ),
                })
              }
              className="inline-flex h-10 items-center justify-center gap-1.5 whitespace-nowrap rounded-button bg-gradient px-3.5 text-[0.82rem] font-medium text-white shadow-[0_6px_18px_rgba(51,40,154,0.24)] transition-all duration-300 hover:-translate-y-0.5 hover:brightness-110"
            >
              <HeroIcons.ArrowTrendingUpIcon className="h-4 w-4" />
              {resolvedMobileCtaLabel}
            </Link>

            <button
              type="button"
              onClick={onToggleMenu}
              className="group relative inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-button border border-white/80 bg-white/25 shadow-[inset_0_1px_0_rgba(255,255,255,0.7)] backdrop-blur-md transition hover:bg-white/45"
              aria-label={menuAriaLabel}
              aria-expanded={isMenuOpen}
            >
              <span
                className={`absolute h-0.5 w-5 rounded-full bg-[#293254] transition-all duration-300 ${
                  isMenuOpen ? 'translate-y-0 rotate-45' : '-translate-y-1.5'
                }`}
              />
              <span
                className={`absolute h-0.5 w-5 rounded-full bg-[#293254] transition-all duration-300 ${
                  isMenuOpen ? 'scale-x-0 opacity-0' : 'scale-x-100 opacity-100'
                }`}
              />
              <span
                className={`absolute h-0.5 w-5 rounded-full bg-[#293254] transition-all duration-300 ${
                  isMenuOpen ? 'translate-y-0 -rotate-45' : 'translate-y-1.5'
                }`}
              />
            </button>
          </div>
        </div>

        <div
          className={`mt-2 grid overflow-hidden rounded-2xl border transition-[grid-template-rows,opacity,border-color] duration-300 ease-out desktop:hidden ${
            isMenuOpen
              ? 'grid-rows-[1fr] opacity-100'
              : 'pointer-events-none grid-rows-[0fr] border-transparent bg-transparent opacity-0'
          }`}
          style={
            isMenuOpen
              ? { ...FROSTED_GLASS, backgroundColor: 'rgba(255,255,255,0.86)' }
              : undefined
          }
        >
          <div className="overflow-hidden px-4 py-4 text-sm text-[#293254]">
            {mobileLinks.map(({ href, label, target, skipLocaleHandling }) => (
              <Link
                key={`${href}-${label}`}
                href={href}
                target={target}
                skipLocaleHandling={skipLocaleHandling}
                onClick={() =>
                  gtmService.ctaClicked({
                    ctaId: `header_mobile_nav_${label}`,
                    ctaLabel: label,
                    ctaLocation: 'header_mobile_menu',
                    destinationUrl: href,
                    destinationType: getDestinationType(href),
                  })
                }
                className="block py-2.5 text-[0.97rem] font-medium text-[#293254] transition-colors hover:text-sheltersBlue"
              >
                {label}
              </Link>
            ))}

            <div className="mt-3 flex items-center justify-between border-t border-white/20 pt-3">
              <div>
                <LanguageSelect mode="toggle" />
              </div>
              <Link
                href={resolvedCtaLink?.href || '#'}
                target={resolvedCtaLink?.target}
                skipLocaleHandling={resolvedCtaLink?.skipLocaleHandling}
                onClick={() =>
                  gtmService.ctaClicked({
                    ctaId: 'header_mobile_menu_cta',
                    ctaLabel: mobileMenuCtaLabel,
                    ctaLocation: 'header_mobile_menu',
                    destinationUrl: resolvedCtaLink?.href || '#',
                    destinationType: getDestinationType(
                      resolvedCtaLink?.href || '#',
                    ),
                  })
                }
                className="inline-flex h-10 items-center justify-center rounded-button bg-gradient px-4 text-[0.9rem] font-medium text-white shadow-[0_6px_18px_rgba(51,40,154,0.24)] transition-all duration-300 hover:-translate-y-0.5 hover:brightness-110"
              >
                {mobileMenuCtaLabel}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </nav>
  )
}
